import React from 'react';
import { VELLUM as T, MONO } from '../folio/kit';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  /* The leading edge of the mark. Defaults to the folio accent; buttons
     that sit on an accent fill pass "white" so the mark stays legible. */
  color?: string;
  text?: string;
}

const SIZES: Record<'sm' | 'md' | 'lg', number> = { sm: 16, md: 28, lg: 44 };

/* The drafting mark.

   A square instrument ring whose leading edge steps a quarter turn at a
   time - not a rounded CSS spinner in --twc-cambridge over border-gray-200,
   which is the one piece of the old palette that every page still showed
   on the way in. Sharp corners, folio ink, and a stepped rotation, because
   an instrument ticks and a square that turns through its diagonals reads
   as a wobble rather than a movement. */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color,
  text,
}) => {
  const px = SIZES[size];
  const weight = size === 'lg' ? 2.5 : 2;

  return (
    <div
      role="status"
      aria-label={text || 'Loading'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: text ? 14 : 0,
      }}
    >
      <span
        aria-hidden
        className="aca-tick"
        style={{
          width: px,
          height: px,
          boxSizing: 'border-box',
          border: `${weight}px solid ${T.rule}`,
          borderTopColor: color || T.accent,
          borderRadius: 0,
          animation: 'aca-tick 1.2s steps(4, end) infinite',
        }}
      />
      {text && (
        <span
          style={{
            fontFamily: MONO,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: T.mute,
          }}
        >
          {text}
        </span>
      )}
    </div>
  );
};

export default LoadingSpinner;
